import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { useEffect } from "react";
import { initUtmTracking } from "@/lib/utm-tracking";
import { NotFoundPage } from "@/components/site/NotFoundPage";
import { ScrollDepthTracker } from "@/components/site/ScrollDepthTracker";
import { VideoEngagementTracker } from "@/components/site/VideoEngagementTracker";
import { UTM_INLINE_CAPTURE } from "@/lib/utm-inline-script";
import { ZARAZ_CONSENT_BRIDGE } from "@/lib/zaraz-consent-bridge";
import { STALE_CHUNK_RELOAD } from "@/lib/stale-chunk-reload";
import { organizationSchema, safeJsonLd } from "@/lib/structured-data-core";
import { LOGO } from "@/lib/site-core";
import faviconAsset from "@/assets/favicon.png.asset.json";
import poppins400 from "@fontsource/poppins/files/poppins-latin-400-normal.woff2?url";
import poppins500 from "@fontsource/poppins/files/poppins-latin-500-normal.woff2?url";
import poppins600 from "@fontsource/poppins/files/poppins-latin-600-normal.woff2?url";
import poppins700 from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url";
import { absoluteUrl } from "@/lib/absolute-url";

function NotFoundComponent() {
  return <NotFoundPage />;
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/locations/restore-northeast/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

// og:url for every page: the page path on the site origin, with the site's
// trailing slash. Child routes that set their own og:url keep it, because the
// deepest route wins for each meta property.
function ogUrlFor(pathname: string | undefined): string {
  const path = !pathname || pathname === "/" ? "/" : pathname.endsWith("/") ? pathname : `${pathname}/`;
  return absoluteUrl(path);
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "D0RgxDwAbJTiPm31UxwDarHp5EATxsagcXw9zQoWCsc" },
      { title: "RESTORE Hair | #1 Hair Transplants, Trusted by Celebrities" },
      { name: "description", content: "Restore your confidence with the top hair restoration team. Get your hair back when you get a hair transplant procedure with RESTORE Hair." },
      { property: "og:title", content: "RESTORE Hair | #1 Hair Transplants, Trusted by Celebrities" },
      { property: "og:description", content: "Restore your confidence with the top hair restoration team. Get your hair back when you get a hair transplant procedure with RESTORE Hair." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: ogUrlFor(matches[matches.length - 1]?.pathname) },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "RESTORE Hair | #1 Hair Transplants, Trusted by Celebrities" },
      { name: "twitter:description", content: "Restore your confidence with the top hair restoration team. Get your hair back when you get a hair transplant procedure with RESTORE Hair." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/a900e1d7-d1f1-4453-8b5f-207cf5929a1c/id-preview-39c96a4a--a5cd4ee2-ca39-4fc0-8cff-b0def42dd345.lovable.app-1779734674220.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/a900e1d7-d1f1-4453-8b5f-207cf5929a1c/id-preview-39c96a4a--a5cd4ee2-ca39-4fc0-8cff-b0def42dd345.lovable.app-1779734674220.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },

      { rel: "icon", type: "image/png", href: faviconAsset.url },
      { rel: "apple-touch-icon", href: faviconAsset.url },
      // Break the CSS -> font request chain flagged by Lighthouse.
      { rel: "preload", as: "font", type: "font/woff2", href: poppins400, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: poppins500, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: poppins600, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: poppins700, crossOrigin: "anonymous" },
      // Images/scripts are fetched no-cors, so these must NOT carry crossorigin
      // or the browser opens a second, unused connection.
      { rel: "preconnect", href: "https://assets.restorehair.com" },
      { rel: "preconnect", href: "https://track.gaconnector.com" },
      { rel: "preconnect", href: "https://tracker.gaconnector.com" },
      { rel: "dns-prefetch", href: "https://track.gaconnector.com" },
      { rel: "dns-prefetch", href: "https://tracker.gaconnector.com" },
      { rel: "preconnect", href: "https://cdn.cookielaw.org" },
      { rel: "dns-prefetch", href: "https://cdn.cookielaw.org" },

    ],
    scripts: [
      // First, so that it catches a missing chunk from an older build.
      { children: STALE_CHUNK_RELOAD },
      // Runs synchronously in <head>, before hydration/router/tag manager,
      // so click IDs + UTMs survive any client-side URL rewrite.
      { children: UTM_INLINE_CAPTURE },
      // OneTrust -> Zaraz consent bridge. Kept in site code (not a Zaraz
      // Custom HTML tool) so gating never depends on a Zaraz tool. Wraps,
      // never overwrites, any existing OptanonWrapper, so it is safe no
      // matter what order OneTrust and Zaraz load in.
      { children: ZARAZ_CONSENT_BRIDGE },
      // Sitewide publisher identity; page-level schemas reference it by @id.
      {
        type: "application/ld+json",
        children: safeJsonLd(organizationSchema(LOGO)),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("react.shell");
  return (
    <html lang="en">
      <head>
        <HeadContent />


      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  // Server only: records that React reached this component (slow request trace).
  (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("react.root");
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    initUtmTracking();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ScrollDepthTracker />
      <VideoEngagementTracker />
      <Outlet />
    </QueryClientProvider>
  );
}
